import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Users, Plus, Trash2, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { fetchPartnerships, fetchAvailablePartnerships, deletePartnershipThunk } from '../../../../features/partnerships/partnershipsThunks';
import { setFilters, setPage, setRowsPerPage, setSearch } from '../../../../features/partnerships/partnershipsSlice';
import { DataTable } from '../../../../components/ui/DataTable';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { CreatePartnershipModal } from './CreatePartnershipModal';

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

export const Partnerships = () => {
  const dispatch = useDispatch();

  const { partnerships, totalCount, loading, error, filters } = useSelector(state => state.partnerships);
  const { user } = useSelector(state => state.auth);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Local state for instant UI update on input, debounced for API
  const [localSearch, setLocalSearch] = useState(filters.search || '');
  const debouncedSearch = useDebounce(localSearch, 500);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      dispatch(setSearch(debouncedSearch));
    }
  }, [debouncedSearch, filters.search, dispatch]);

  useEffect(() => {
    if (user?.admin?.id) {
      dispatch(fetchPartnerships(user.admin.id));
      dispatch(fetchAvailablePartnerships());
    }
  }, [dispatch, user?.admin?.id, filters.page, filters.rowsPerPage, filters.search, filters.orderBy, filters.order]);

  const handleDelete = (id) => {
    if (window.confirm("¿Estás seguro que quieres eliminar esta alianza?")) {
      dispatch(deletePartnershipThunk({ adminId: user.admin.id, partnershipId: id }));
    }
  };

  const columns = [
    {
      accessor: 'role',
      header: 'Rol',
      cell: (item) => {
        const isSource = item.role === 'SOURCE';
        if (isSource) {
          return (
            <div className="flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-full text-xs font-semibold bg-ninja-greenLight text-ninja-dark border border-ninja-green/30" title="Tú envías -> Partner Recibe">
              <ArrowUpRight size={14} className="text-ninja-green" />
              <span>Saliente</span>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800" title="Partner Envía -> Tú Recibes">
            <ArrowDownLeft size={14} />
            <span>Entrante</span>
          </div>
        );
      }
    },
    {
      accessor: 'partner',
      header: 'Partner',
      cell: (item) => {
        const partnerData = item.role === 'SOURCE' ? item.target_admin_details : item.source_admin_details;
        return (
          <div>
            <div className="font-bold text-ninja-dark dark:text-white">
              {item.partner_name}
            </div>
            <div className="text-xs text-text-secondary">
              Admin ID: {partnerData?.id || '-'}
            </div>
          </div>
        );
      }
    },
    {
      accessor: 'email',
      header: 'Email Contacto',
      cell: (item) => {
        const partnerData = item.role === 'SOURCE' ? item.target_admin_details : item.source_admin_details;
        return <span className="text-text-secondary">{partnerData?.email || '-'}</span>;
      }
    },
    {
      accessor: 'capacity',
      header: 'Capacidad Máxima',
      cell: (item) => {
        const partnerData = item.role === 'SOURCE' ? item.target_admin_details : item.source_admin_details;
        return <span className="font-medium">{partnerData?.upselling_capacity ?? 0}</span>;
      }
    },
    {
      accessor: 'actions',
      header: 'Acciones',
      cell: (item) => (
        <div className="flex items-center justify-end">
          <PendingButton
            variant="subtle"
            size="sm"
            iconOnly
            icon={Trash2}
            pendingOn={deletePartnershipThunk}
            pendingKey={item.id}
            title="Eliminar Alianza"
            aria-label="Eliminar Alianza"
            className="hover:!text-danger hover:!bg-red-50 dark:hover:!bg-red-900/20"
            onClick={() => handleDelete(item.id)}
          />
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-300">

      <PageHeader
        icon={Users}
        iconClassName="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
        title="Mis Alianzas de Despacho"
        description="Gestiona tus alianzas estratégicas entrantes y salientes."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setIsModalOpen(true)}>
            Crear Alianza
          </Button>
        }
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-danger p-4 rounded-xl border border-red-200 dark:border-red-900 flex items-center gap-3">
          <span className="font-semibold">Error:</span> {error}
        </div>
      )}

      <FilterBar variant="bare">
        <SearchInput
          variant="bare"
          value={localSearch}
          onChange={setLocalSearch}
          placeholder="Buscar alianzas..."
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={partnerships}
        isLoading={loading}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        onPageChange={(page) => dispatch(setPage(page))}
        onRowsPerPageChange={(rows) => dispatch(setRowsPerPage(rows))}
        itemLabel="alianzas"
      />

      <CreatePartnershipModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};
